import React, { useEffect, useRef, useState } from "react";
import { X, Video, VideoOff, Mic, MicOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMediaStream } from "@/hooks/useMediaStream";
import { cn } from "@/lib/utils";

interface GoLiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoLive: (stream: MediaStream) => void;
  sceneEmoji: string;
  sceneName: string;
  cityName: string;
}

const GoLiveModal: React.FC<GoLiveModalProps> = ({
  isOpen,
  onClose,
  onGoLive,
  sceneEmoji,
  sceneName,
  cityName,
}) => {
  const {
    stream,
    videoEnabled,
    audioEnabled,
    isLoading,
    error,
    permissionStatus,
    startStream,
    stopStream,
    toggleVideo,
    toggleAudio,
  } = useMediaStream();

  const videoRef = useRef<HTMLVideoElement>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Request stream when modal opens
  useEffect(() => {
    if (isOpen && !stream && !isLoading) {
      startStream(true, true);
    }
    return () => {
      if (!isOpen && stream) {
        stopStream();
      }
    };
  }, [isOpen]);

  // Attach stream to video element
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play().catch(console.error);
        setIsReady(true);
      };
    }
  }, [stream]);

  // Handle close
  const handleClose = () => {
    stopStream();
    setIsReady(false);
    setCountdown(null);
    onClose();
  };

  // Handle Go Live with countdown
  const handleGoLive = () => {
    if (!stream || !videoEnabled) return;

    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          // Pass the stream to parent
          onGoLive(stream);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md bg-gray-900 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{sceneEmoji}</span>
            <div>
              <h2 className="text-white font-semibold">Go Live</h2>
              <p className="text-white/60 text-sm">{cityName} • {sceneName}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Camera Preview */}
        <div className="relative aspect-[4/3] bg-black">
          {/* Video Element */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={cn(
              "w-full h-full object-cover transform scale-x-[-1]",
              !videoEnabled && "hidden"
            )}
          />

          {/* Loading State */}
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900">
              <Loader2 className="w-10 h-10 text-white animate-spin mb-3" />
              <p className="text-white/70 text-sm">Requesting camera access...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 p-6 text-center">
              <VideoOff className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-white font-medium mb-2">Camera Access Required</p>
              <p className="text-white/60 text-sm mb-4">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => startStream(true, true)}
                className="border-white/30 text-white hover:bg-white/10"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Video Off State */}
          {stream && !videoEnabled && !isLoading && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800">
              <VideoOff className="w-16 h-16 text-white/40 mb-3" />
              <p className="text-white/60 text-sm">Camera is off</p>
            </div>
          )}

          {/* Countdown Overlay */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70">
              <div className="text-center">
                <div className="text-7xl font-bold text-white animate-pulse mb-2">
                  {countdown}
                </div>
                <p className="text-white/70">Going live...</p>
              </div>
            </div>
          )}

          {/* Controls Overlay */}
          {stream && !countdown && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={toggleAudio}
                className={cn(
                  "rounded-full w-12 h-12 border-2",
                  audioEnabled
                    ? "border-white/30 bg-white/10 text-white hover:bg-white/20"
                    : "border-red-500 bg-red-500/20 text-red-400 hover:bg-red-500/30"
                )}
              >
                {audioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={toggleVideo}
                className={cn(
                  "rounded-full w-12 h-12 border-2",
                  videoEnabled
                    ? "border-white/30 bg-white/10 text-white hover:bg-white/20"
                    : "border-red-500 bg-red-500/20 text-red-400 hover:bg-red-500/30"
                )}
              >
                {videoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 space-y-3">
          {/* Info Note */}
          <p className="text-white/50 text-xs text-center">
            {videoEnabled
              ? "You'll be visible to others when you go live"
              : "Turn on your camera to go live"}
          </p>

          {/* Go Live Button */}
          <Button
            onClick={handleGoLive}
            disabled={!isReady || !videoEnabled || countdown !== null || isLoading}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold py-6 text-lg"
          >
            {countdown !== null ? (
              "Starting..."
            ) : isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Video className="w-5 h-5 mr-2" />
                Go Live
              </>
            )}
          </Button>

          {/* Cancel */}
          <Button
            variant="ghost"
            onClick={handleClose}
            className="w-full text-white/60 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GoLiveModal;
